import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, Search, Bell, HelpCircle, Loader2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { fetchUnreadCount, fetchNotifications, markNotificationRead, markAllNotificationsRead } from '@/hooks/useTickets';
import type { NotificationItem } from '@uniticket/shared';
import { APP_VERSION } from '@uniticket/shared';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { GlobalSearchDialog } from '@/components/search/GlobalSearchDialog';
import { ChangelogDialog } from '@/components/custom/ChangelogDialog';

interface TopBarProps {
  title?: string;
  onMenuClick: () => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function TopBar({ title, onMenuClick }: TopBarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [showPanel, setShowPanel] = useState(false);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Ctrl+K / Cmd+K keyboard shortcut to open search
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Poll unread count every 30s
  const pollUnread = useCallback(() => {
    fetchUnreadCount()
      .then((res) => setUnreadCount(res.count))
      .catch(() => {}); // silent fail for polling
  }, []);

  useEffect(() => {
    pollUnread();
    const interval = setInterval(pollUnread, 30000);
    return () => clearInterval(interval);
  }, [pollUnread]);

  // Close panel on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setShowPanel(false);
      }
    }
    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPanel]);

  async function togglePanel() {
    if (showPanel) {
      setShowPanel(false);
      return;
    }
    setShowPanel(true);
    setLoadingNotifs(true);
    try {
      const res = await fetchNotifications({ limit: '10' });
      setNotifications(res.data);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoadingNotifs(false);
    }
  }

  async function handleNotificationClick(n: NotificationItem) {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
        setUnreadCount((c) => Math.max(0, c - 1));
        setNotifications((prev) => prev.map((x) => x.id === n.id ? { ...x, isRead: true } : x));
      } catch {}
    }
    setShowPanel(false);
    if (n.ticketId) {
      navigate(`/tickets/${n.ticketId}`);
    }
  }

  async function handleMarkAllRead() {
    try {
      const res = await markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((x) => ({ ...x, isRead: true })));
      toast.success(`Marked ${res.count} notifications as read`);
    } catch {
      toast.error('Failed to mark all as read');
    }
  }

  async function handleSignOut() {
    await logout();
    navigate('/login');
  }

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between h-16 px-4 md:px-8 bg-white border-b border-slate-200">
      {/* Left: hamburger (mobile) + title */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuClick}
          className="md:hidden p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label={t('topbar.openMenu')}
        >
          <Menu className="w-5 h-5" />
        </button>
        {title && <h1 className="text-xl md:text-2xl font-bold gradient-text">{title}</h1>}
      </div>

      {/* Right: search + notifications + avatar */}
      <div className="flex items-center gap-3">
        {/* Search (desktop only) */}
        <div className="hidden md:flex relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="w-64 pl-10 pr-14 h-9 text-sm text-left text-slate-400 bg-white border border-slate-200 rounded-md hover:border-slate-300 transition-colors cursor-pointer"
          >
            {t('topbar.searchPlaceholder')}
          </button>
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 border border-slate-200 rounded">
            Ctrl+K
          </kbd>
        </div>
        <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

        {/* Notifications bell */}
        <div className="relative" ref={panelRef}>
          <button
            type="button"
            onClick={togglePanel}
            className="relative p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label={t('topbar.notifications')}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-white bg-brand-rose rounded-full">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification dropdown panel */}
          {showPanel && (
            <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-slate-800">{t('topbar.notifications')}</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs font-medium text-brand-rose hover:text-brand-purple transition-colors"
                  >
                    {t('topbar.markAllRead')}
                  </button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto">
                {loadingNotifs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : notifications.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">{t('topbar.noNotifications')}</p>
                ) : (
                  notifications.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                        !n.isRead ? 'bg-brand-rose/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {!n.isRead && (
                          <span className="mt-1.5 w-2 h-2 bg-brand-rose rounded-full shrink-0" />
                        )}
                        <div className={`flex-1 min-w-0 ${n.isRead ? 'ml-5' : ''}`}>
                          <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                            {n.title}
                          </p>
                          {n.body && (
                            <p className="text-xs text-slate-400 mt-0.5 truncate">{n.body}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {n.ticketNumber && (
                              <span className="text-[10px] font-mono font-medium text-brand-rose">{n.ticketNumber}</span>
                            )}
                            <span className="text-[10px] text-slate-400">{timeAgo(n.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <button
                onClick={() => { setShowPanel(false); navigate('/notifications'); }}
                className="w-full px-4 py-2.5 text-xs font-medium text-brand-rose hover:bg-slate-50 border-t border-slate-100 transition-colors"
              >
                {t('topbar.viewAll')}
              </button>
            </div>
          )}
        </div>

        {/* Help button */}
        <button
          type="button"
          onClick={() => navigate('/help')}
          className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label={t('topbar.helpGuide')}
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        {/* User avatar dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100 transition-colors">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-[10px]">
                  {user ? getInitials(user.displayName) : '?'}
                </AvatarFallback>
              </Avatar>
              <span className="hidden lg:block text-sm font-medium text-slate-700">
                {user?.displayName ?? ''}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {user && (
              <>
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.displayName}</p>
                  <p className="text-xs text-slate-500">{user.operatorName}</p>
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => navigate('/profile')}>{t('topbar.profile')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/profile/change-password')}>{t('topbar.changePassword')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/profile')}>{t('topbar.preferences')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/help')}>{t('topbar.helpGuide')}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/privacy')}>{t('footer.privacy')}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>{t('topbar.signOut')}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                // Let the dropdown close + release its focus trap, then open the dialog on the
                // next tick so the two Radix focus traps never overlap.
                setTimeout(() => setChangelogOpen(true), 0);
              }}
              className="cursor-pointer justify-center gap-1 text-[10px] text-slate-400 focus:text-brand-rose"
            >
              <Sparkles className="h-3 w-3" />
              UniTicket v{APP_VERSION}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
      </div>
    </header>
  );
}
